import type { PushDevice } from '@nexus/core';

/**
 * Push messages in the browser (ADR-0058). The service worker shows them
 * (apps/web/public/push-sw.js); this file asks for permission and manages the subscription.
 */

/** What this browser can do: push, push after installing to the home screen, or nothing. */
export type PushSupport = 'supported' | 'install' | 'unsupported';

/** iPhone and iPad, also an iPad that reports itself as a Mac. */
function appleMobile(): boolean {
  return (
    /iPhone|iPad|iPod/.test(navigator.userAgent) ||
    (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1)
  );
}

/** Opened from the home screen or as installed app, not in a browser tab. */
export function standalone(): boolean {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches === true ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

export function pushSupport(): PushSupport {
  // iPhone and iPad offer push only to apps on the home screen (iOS 16.4 and later).
  if (appleMobile() && !standalone()) return 'install';
  const supported =
    'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  return supported ? 'supported' : 'unsupported';
}

/** Whether the person or the device blocked notifications for the app. */
export function pushBlocked(): boolean {
  return 'Notification' in window && Notification.permission === 'denied';
}

export function deviceKind(): PushDevice {
  const agent = navigator.userAgent;
  if (/iPhone|iPod/.test(agent)) return 'iphone';
  if (/iPad/.test(agent) || (/Macintosh/.test(agent) && navigator.maxTouchPoints > 1)) {
    return 'ipad';
  }
  if (/Android/.test(agent)) return 'android';
  if (/Windows/.test(agent)) return 'windows';
  if (/Macintosh|Mac OS X/.test(agent)) return 'mac';
  if (/Linux|CrOS/.test(agent)) return 'linux';
  return 'other';
}

/** Why push could not be switched on. */
export class PushSetupError extends Error {
  override name = 'PushSetupError';

  constructor(readonly code: 'denied' | 'dismissed' | 'no-worker') {
    super(code);
  }
}

/** The service worker; `undefined` where none runs, e.g. on a development server. */
async function registration(): Promise<ServiceWorkerRegistration | undefined> {
  if (!('serviceWorker' in navigator)) return undefined;
  const existing = await navigator.serviceWorker.getRegistration();
  if (existing?.active) return existing;
  // Right after the first start the worker may still be installing.
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise<undefined>((resolve) => setTimeout(() => resolve(undefined), 5000)),
  ]);
}

function bytes(base64url: string): Uint8Array<ArrayBuffer> {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(base64 + '='.repeat((4 - (base64.length % 4)) % 4));
  return Uint8Array.from(binary, (char) => char.charCodeAt(0));
}

function sameKey(subscription: PushSubscription, key: Uint8Array): boolean {
  const current = subscription.options.applicationServerKey;
  if (!current) return true;
  const view = new Uint8Array(current);
  return view.length === key.length && view.every((value, index) => value === key[index]);
}

/**
 * Asks for permission and subscribes this browser. Must run straight from a click: Safari shows
 * the question only for a user gesture, so nothing is awaited before it.
 */
export async function subscribePush(publicKey: string): Promise<PushSubscriptionJSON> {
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new PushSetupError(permission === 'denied' ? 'denied' : 'dismissed');
  }
  const worker = await registration();
  if (!worker) throw new PushSetupError('no-worker');
  const key = bytes(publicKey);
  let subscription = await worker.pushManager.getSubscription();
  // A subscription for another key (e.g. from a development server) cannot be reused.
  if (subscription && !sameKey(subscription, key)) {
    await subscription.unsubscribe();
    subscription = null;
  }
  subscription ??= await worker.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: key,
  });
  return subscription.toJSON();
}

/** The address of this browser at its push service, `null` without subscription. */
export async function currentEndpoint(): Promise<string | null> {
  if (pushSupport() !== 'supported') return null;
  const worker = await navigator.serviceWorker.getRegistration();
  const subscription = await worker?.pushManager.getSubscription();
  return subscription?.endpoint ?? null;
}

/** Ends the subscription in the browser, so this device gets nothing anymore. */
export async function unsubscribePush(): Promise<void> {
  if (pushSupport() !== 'supported') return;
  const worker = await navigator.serviceWorker.getRegistration();
  await (await worker?.pushManager.getSubscription())?.unsubscribe();
}

/** Sets the number on the app icon (home screen, dock, taskbar) where the device supports it. */
export function setAppBadge(count: number): void {
  const badging = navigator as Navigator & {
    setAppBadge?: (count?: number) => Promise<void>;
    clearAppBadge?: () => Promise<void>;
  };
  const done = count > 0 ? badging.setAppBadge?.(count) : badging.clearAppBadge?.();
  void done?.catch(() => undefined);
}

const PROMPT_KEY = 'nexus.pushPrompt';

/** Whether the person answered the question after the installation with "Später" on this device. */
export function promptDismissed(): boolean {
  try {
    return localStorage.getItem(PROMPT_KEY) === 'dismissed';
  } catch {
    return false;
  }
}

/** Asks no more on this device; also when push was switched off here on purpose. */
export function dismissPrompt(): void {
  try {
    localStorage.setItem(PROMPT_KEY, 'dismissed');
  } catch {
    // Without storage the question comes again at the next start.
  }
}
